// Client-side helper for fetching stock metadata (symbol, name, sector, board,
// last price/change/% and last update date for every emiten) directly from
// Azure Blob Storage (trading-data-v2/raw/stockmetadata.parquet), using a SAS
// token issued by the backend gate endpoint (GET /api/watchlist/sas-token).
//
// Backs both the dashboard watchlist (price/change/name) and the global
// stock symbol list (frontend/src/data/stockList.ts), replacing the previous
// /api/watchlist and /api/stock/emiten-list backend round-trips.

import { parquetRead, parquetMetadata } from 'hyparquet';
import { compressors } from 'hyparquet-compressors';
import { api } from '../services/api';
import { blobCacheKey } from './cacheKey';

const STOCK_METADATA_PATH = 'raw/stockmetadata.parquet';
const CACHE_NAME = 'stock-metadata-parquet-cache';

export interface StockMetadataRow {
  symbol: string;
  name: string;
  sector: string;
  board: string;
  shares: number;
  price: number;
  change: number;
  changePercent: number;
  lastUpdate?: string;
}

function localDateKey(d = new Date()): string {
  // Cache for 15 minutes instead of the entire calendar day
  // This ensures users get the latest EOD data shortly after it's updated on the server
  const quarterHour = Math.floor(d.getMinutes() / 15);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}-${d.getHours()}-${quarterHour}`;
}

function asyncBufferFromArrayBuffer(arrayBuffer: ArrayBuffer) {
  return {
    byteLength: arrayBuffer.byteLength,
    slice: (start: number, end?: number): Promise<ArrayBuffer> =>
      Promise.resolve(arrayBuffer.slice(start, end)),
  };
}

// In-memory cache, valid for the current calendar day, so switching pages
// within the same session never re-downloads/re-parses the file.
let cachedRows: StockMetadataRow[] | null = null;
let cachedDate: string | null = null;
let loadPromise: Promise<StockMetadataRow[]> | null = null;

async function parseArrayBuffer(arrayBuffer: ArrayBuffer): Promise<StockMetadataRow[]> {
  const metadata = parquetMetadata(arrayBuffer as any);
  const columnNames = (metadata.schema || []).slice(1).map((f: any) => f.name);
  const asyncBuf = asyncBufferFromArrayBuffer(arrayBuffer);
  const rawRows: any[][] = [];
  await parquetRead({
    file: asyncBuf,
    compressors,
    onComplete: (data: any[][]) => {
      for (const row of data) {
        if (row) {
          rawRows.push(row);
        }
      }
    },
  });

  return rawRows.map(rowArr => {
    const obj: Record<string, any> = {};
    columnNames.forEach((col, i) => {
      let v = rowArr[i];
      if (typeof v === 'bigint') v = Number(v);
      obj[col] = v;
    });

    const row: StockMetadataRow = {
      symbol: String(obj['symbol'] ?? '').toUpperCase(),
      name: String(obj['name'] ?? ''),
      sector: String(obj['sector'] ?? ''),
      board: String(obj['board'] ?? ''),
      shares: Number(obj['shares']) || 0,
      price: Number(obj['last_price']) || 0,
      change: Number(obj['change']) || 0,
      changePercent: Number(obj['change_percent']) || 0,
    };
    const lastUpdate = obj['last_update'];
    if (lastUpdate !== undefined && lastUpdate !== null) {
      row.lastUpdate = String(lastUpdate);
    }
    return row;
  });
}

const FALLBACK_CACHE_KEY = `${STOCK_METADATA_PATH}::latest`;

async function fetchAndParse(): Promise<StockMetadataRow[]> {
  const cache = await caches.open(CACHE_NAME);

  // 1. Try today's cache
  const todayKey = localDateKey();
  const todayCacheKey = `${STOCK_METADATA_PATH}::${todayKey}`;
  const todayHit = await cache.match(blobCacheKey(todayCacheKey));
  if (todayHit) {
    return parseArrayBuffer(await todayHit.arrayBuffer());
  }

  // 2. Try fresh fetch from blob
  let arrayBuffer: ArrayBuffer | null = null;
  try {
    const tokenRes = await api.getBrokerSummarySasToken();
    if (!tokenRes.success || !tokenRes.data) throw new Error(tokenRes.error || 'Failed to get SAS token');
    const { containerUrl, sasToken } = tokenRes.data;
    const isDev = (import.meta as any).env?.DEV || false;
    const blobUrl = isDev
      ? `/azure-blob-proxy/trading-data-v2/${STOCK_METADATA_PATH}?${sasToken}`
      : `${containerUrl}/${STOCK_METADATA_PATH}?${sasToken}`;

    const response = await fetch(blobUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    arrayBuffer = await response.arrayBuffer();

    // Cache under today's key and as stable "latest" fallback
    const buf = arrayBuffer.slice(0);
    await cache.put(blobCacheKey(todayCacheKey), new Response(buf, { headers: { 'Content-Type': 'application/octet-stream' } }));
    await cache.put(blobCacheKey(FALLBACK_CACHE_KEY), new Response(arrayBuffer.slice(0), { headers: { 'Content-Type': 'application/octet-stream' } }));
  } catch (fetchErr) {
    console.warn('[stockMetadata] blob fetch failed, trying last cached data:', fetchErr);
  }

  // 3. Fall back to last known-good cache if fetch failed
  if (!arrayBuffer) {
    const fallbackHit = await cache.match(blobCacheKey(FALLBACK_CACHE_KEY));
    if (fallbackHit) {
      console.warn('[stockMetadata] using cached fallback (last available data)');
      return parseArrayBuffer(await fallbackHit.arrayBuffer());
    }
    throw new Error('stockmetadata.parquet unavailable and no cached fallback found');
  }

  return parseArrayBuffer(arrayBuffer);
}

/**
 * Fetch (or return cached) stock metadata for every emiten. Cached in-memory
 * for the current calendar day and persisted via the Cache API across reloads.
 */
export async function fetchStockMetadata(forceRefresh = false): Promise<StockMetadataRow[]> {
  if (forceRefresh) {
    cachedRows = null;
    loadPromise = null;
    try {
      const cache = await caches.open(CACHE_NAME);
      const keys = await cache.keys();
      for (const req of keys) {
        if (req.url.includes(STOCK_METADATA_PATH)) {
          await cache.delete(req);
        }
      }
    } catch (e) {
      console.warn('Failed to clear Cache API for stockmetadata', e);
    }
  } else {
    if (cachedRows) {
      return cachedRows;
    }
    if (loadPromise) {
      return loadPromise;
    }
  }

  loadPromise = (async () => {
    try {
      const rows = await fetchAndParse();
      cachedRows = rows;
      return rows;
    } finally {
      loadPromise = null;
    }
  })();

  return loadPromise;
}

/**
 * Sorted list of all emiten symbols, derived from stock metadata.
 * Replacement for the previous /api/stock/emiten-list endpoint.
 */
export async function fetchStockSymbols(): Promise<string[]> {
  const rows = await fetchStockMetadata();
  return Array.from(new Set(rows.map(r => r.symbol).filter(Boolean))).sort();
}

/**
 * Number of listed shares for a given stock code, or 0 if not found.
 * Replacement for the previous emiten_detail_list.csv-based getEmitenShares.
 */
export async function getEmitenShares(symbol: string): Promise<number> {
  const rows = await fetchStockMetadata();
  return rows.find(r => r.symbol === symbol.toUpperCase())?.shares ?? 0;
}

export interface SectorMappingData {
  stockToSector: { [stock: string]: string };
  sectors: string[];
  sectorMapping: { [sector: string]: string[] };
}

/**
 * Sector mapping (stock -> sector, sector -> stocks, sector list), derived
 * from stock metadata. Replacement for the previous /api/stock/sector-mapping
 * endpoint.
 */
export async function fetchSectorMapping(forceRefresh = false): Promise<{ success: true; data: SectorMappingData }> {
  const rows = await fetchStockMetadata(forceRefresh);
  const stockToSector: { [stock: string]: string } = {};
  const sectorMapping: { [sector: string]: string[] } = {};

  rows.forEach(r => {
    if (!r.symbol || !r.sector) return;
    stockToSector[r.symbol] = r.sector;
    if (!sectorMapping[r.sector]) sectorMapping[r.sector] = [];
    sectorMapping[r.sector]!.push(r.symbol);
  });

  // IDX is an aggregate index (not a regular stock) but appears as its own "sector"
  if (!sectorMapping['IDX']) sectorMapping['IDX'] = ['IDX'];
  stockToSector['IDX'] = 'IDX';

  const sectors = Object.keys(sectorMapping).filter(s => sectorMapping[s]!.length > 0).sort();

  return { success: true, data: { stockToSector, sectors, sectorMapping } };
}
